import type {
  InvitationConfig,
  InvitationPhoto,
  InvitationTimelineEntry,
} from "../types/invitation";

export function newInvitation(
  template: InvitationConfig["template"] = "above-the-clouds",
): InvitationConfig {
  return {
    version: 1,
    template,
    firstName: "",
    secondName: "",
    message:
      "We would love for you to join us as we celebrate a lifetime of little moments, together.",
    rsvpDeadline: "",
    palette: "rose",
    showGiftRegistry: true,
    gentleMotion: false,
    timeline: [],
  };
}
const isRecord = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === "object" && !Array.isArray(v);
const text = (v: unknown, max: number) =>
  typeof v === "string" ? v.slice(0, max) : "";
export const validTime = (v: string) => /^([01]\d|2[0-3]):[0-5]\d$/.test(v);
export function validDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T12:00:00Z`);
  return (
    !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value
  );
}
function readPhoto(value: unknown): InvitationPhoto | undefined {
  if (
    !isRecord(value) ||
    typeof value.url !== "string" ||
    value.url.length > 2048
  )
    return;
  try {
    const url = new URL(value.url);
    if (url.protocol !== "https:" || url.username || url.password) return;
    return {
      id: text(value.id, 80) || url.href,
      url: url.href,
      caption: text(value.caption, 160),
    };
  } catch {
    return;
  }
}
export function readInvitation(value: unknown): InvitationConfig | undefined {
  if (
    !isRecord(value) ||
    value.version !== 1 ||
    (value.template !== "above-the-clouds" &&
      value.template !== "timeless-ivory" &&
      value.template !== "midnight-garden" &&
      value.template !== "champagne-reverie" &&
      value.template !== "tuscan-sunset" &&
      value.template !== "celestial-vows" &&
      value.template !== "royal-kandyan-arrival" &&
      value.template !== "lotus-poruwa" &&
      value.template !== "araliya-courtyard" &&
      value.template !== "ceylon-heritage")
  )
    return undefined;
  const base = newInvitation(value.template);
  const timeline: InvitationTimelineEntry[] = [];
  if (Array.isArray(value.timeline))
    value.timeline.slice(0, 20).forEach((entry, i) => {
      if (!isRecord(entry)) return;
      const time = text(entry.time, 5),
        title = text(entry.title, 100),
        eventId = text(entry.eventId, 80);
      if ((!validTime(time) || !title.trim()) && !eventId) return;
      timeline.push({
        id: text(entry.id, 80) || `entry-${i}`,
        title,
        time,
        date: validDate(String(entry.date)) ? String(entry.date) : undefined,
        eventId: eventId || undefined,
      });
    });
  return {
    ...base,
    firstName: text(value.firstName, 60),
    secondName: text(value.secondName, 60),
    message: text(value.message, 1000) || base.message,
    rsvpDeadline: validDate(String(value.rsvpDeadline))
      ? String(value.rsvpDeadline)
      : "",
    palette: value.palette === "mauve" ? "mauve" : "rose",
    showGiftRegistry: value.showGiftRegistry !== false,
    gentleMotion: value.gentleMotion === true,
    timeline,
    story: Array.isArray(value.story) ? value.story.slice(0, 6).flatMap((entry, i) =>
      isRecord(entry) && text(entry.title, 80).trim() ? [{ id: text(entry.id, 80) || `story-${i}`, title: text(entry.title, 80), detail: text(entry.detail, 240) }] : []) : [],
    countdownAt: countdownTimestamp(value.countdownAt) !== undefined ? String(value.countdownAt) : "",
    entrancePetalsEnabled: value.entrancePetalsEnabled !== false,
    petalsEnabled: value.petalsEnabled !== false,
    soundtrackUrl: readSoundtrack(value.soundtrackUrl)?.url || "",
    featuredPhoto: readPhoto(value.featuredPhoto),
    photos: Array.isArray(value.photos)
      ? value.photos.slice(0, 6).flatMap((p) => {
          const photo = readPhoto(p);
          return photo ? [photo] : [];
        })
      : [],
  };
}
export function formatInvitationDate(date: string): string {
  return validDate(date)
    ? new Intl.DateTimeFormat("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: "UTC",
      }).format(new Date(`${date}T12:00:00Z`))
    : date;
}
export function formatInvitationTime(time: string): string {
  if (!validTime(time.slice(0, 5))) return time;
  const [h, m] = time.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
}
export function publicTimeline(
  config: InvitationConfig,
  events: { id: string; name: string; date: string; start_time: string }[],
  fallbackDate: string,
): InvitationTimelineEntry[] {
  const entries = config.timeline.length
    ? config.timeline
    : events.map((e) => ({
        id: e.id,
        eventId: e.id,
        title: e.name,
        time: e.start_time,
        date: e.date,
      }));
  return entries
    .flatMap((entry) => {
      const event = entry.eventId
        ? events.find((e) => e.id === entry.eventId)
        : undefined;
      if (entry.eventId && !event) return []; // Deleted/private events must not leave stale public details.
      return [
        {
          ...entry,
          title: event?.name || entry.title,
          time: event?.start_time || entry.time,
          date: event?.date || entry.date || fallbackDate,
        },
      ];
    })
    .sort((a, b) => `${a.date}T${a.time}`.localeCompare(`${b.date}T${b.time}`));
}
export function hasGiftRegistry(
  config: InvitationConfig,
  itemCount: number,
  closed: boolean,
): boolean {
  return config.showGiftRegistry && itemCount > 0 && !closed;
}

// The owner enters Sri Lanka local time; viewers always count down to the same instant.
export function countdownTimestamp(value: unknown): number | undefined {
  if (typeof value !== "string" || value.length !== 16 || value[10] !== "T" || !validDate(value.slice(0, 10)) || !validTime(value.slice(11))) return;
  return Date.parse(`${value}:00+05:30`);
}

export function readSoundtrack(value: unknown): { type: "youtube" | "audio"; url: string; videoId?: string } | undefined {
  if (typeof value !== "string" || value.length > 2048 || !value.trim()) return;
  try {
    const url = new URL(value.trim());
    if (url.protocol !== "https:" || url.username || url.password) return;
    const host = url.hostname.toLowerCase();
    const youtube = ["youtube.com", "www.youtube.com", "m.youtube.com", "youtu.be", "www.youtube-nocookie.com"].includes(host);
    if (youtube) {
      const parts = url.pathname.split("/").filter(Boolean);
      const id = host === "youtu.be" ? parts[0] : url.pathname === "/watch" ? url.searchParams.get("v") : ["embed", "shorts", "live"].includes(parts[0]) ? parts[1] : undefined;
      if (!id || !/^[A-Za-z0-9_-]{11}$/.test(id)) return;
      return {type: "youtube", videoId: id, url: `https://www.youtube.com/watch?v=${id}`};
    }
    if (!/\.(mp3|m4a|ogg|wav|aac)$/i.test(url.pathname)) return;
    return {type: "audio", url: url.href};
  } catch { return; }
}
